// src/lib/errors/ErrorReporter.ts

import posthog from 'posthog-js';
import type { ErrorCategory, ErrorReason } from './ErrorClassifier';

export interface ErrorContext {
  /** URL ou endpoint de l'API concerné */
  url?: string;
  /** Action utilisateur qui a déclenché l'erreur */
  action?: string;
  /** Identifiants métier — jamais de contenu utilisateur (RGPD) */
  case_id?: string;
  conversation_id?: string;
  document_id?: string;
  bordereau_id?: string;
  template_pack_id?: string;
  /** Chemin technique de stockage (bucket key type `cabinet_id/uuid.pdf`) — pas un chemin filesystem utilisateur */
  file_path?: string;
  /** Identifiant technique de session WebSocket (non-PII) */
  sessionKey?: string;
  /** Index de pièce dans une opération batch (document split, etc.) */
  pieceIndex?: number;
  /** cabinet_id est automatiquement inclus via posthog.register() — ne pas le passer manuellement */
}

/**
 * Envoie l'erreur vers PostHog Error Tracking (captureException) pour bénéficier
 * du grouping automatique, des stack traces navigables, et du lien session replay.
 *
 * Ne transmet JAMAIS de contenu utilisateur (texte de requête, noms de clients, contenu de docs).
 *
 * Le cabinet_id est propagé automatiquement par PostHog via posthog.register()
 * appelé dans usePostHog.identifyUser() — pas besoin de le passer dans le contexte.
 */
export function reportError(params: {
  category: ErrorCategory;
  reason?: ErrorReason;
  retryable?: boolean;
  statusCode?: number;
  technicalMessage: string;
  stackTrace?: string;
  errorCode?: string;
  requestId?: string;
  context?: ErrorContext;
  /** Objet Error original — nécessaire pour captureException (stack trace native) */
  originalError?: unknown;
}): void {
  const {
    category,
    reason,
    retryable,
    statusCode,
    technicalMessage,
    errorCode,
    requestId,
    context,
    originalError,
  } = params;

  try {
    // Reconstruire un Error si on n'a pas l'original (ex: boundary avec componentStack)
    const error = originalError instanceof Error
      ? originalError
      : new Error(technicalMessage);

    posthog.captureException(error, {
      error_category: category,
      error_reason: reason,
      error_retryable: retryable ?? false,
      error_status_code: statusCode ?? 0,
      error_backend_code: errorCode,
      request_id: requestId,
      url: context?.url ?? window.location.pathname,
      action: context?.action,
      case_id: context?.case_id,
      conversation_id: context?.conversation_id,
      document_id: context?.document_id,
      // Fingerprint custom pour grouper les erreurs business par code backend
      ...(errorCode ? { $exception_fingerprint: `${category}:${errorCode}` } : {}),
    });
  } catch {
    // Ne jamais laisser PostHog crasher l'app — le handler d'erreurs ne peut pas crasher
  }
}
